import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, mxn, fmtDate, exportToExcel } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Btn, Select } from "@/components/Kit";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLeft, FileText, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function EstadoCuenta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("facturas");
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");

  // Filtros del reporte
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [payFilter, setPayFilter] = useState("todo"); // todo | pagados | pendientes

  useEffect(() => {
    api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setData(null);
    const q = orgId ? `organizacion_id=${orgId}` : `client_id=${id}`;
    api.get(`/cobranza-report?mode=general&${q}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id, orgId]);

  if (data === null) return (<><PageHeader overline="Clientes" title="Estado de cuenta" /><div className="p-10 font-mono-data text-[#5B6672]">Cargando…</div></>);
  if (data === false) return (<><PageHeader overline="Clientes" title="Estado de cuenta" /><div className="p-10 font-mono-data text-[#ED1F26]">No se pudo cargar.</div></>);

  const { client, organization, facturas, remisiones } = data;
  const payments = data.payments || [];
  const nombre = orgId ? (organization?.name || "Organización") : (client?.name || "Cliente");
  const num = (v) => Number((v || 0).toFixed(2));
  const money = (v) => `$${(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ---- Filtros compartidos (fecha + estatus de pago) ----
  const inRange = (iso) => {
    const d = (iso || "").slice(0, 10);
    if (dateFrom && d && d < dateFrom) return false;
    if (dateTo && d && d > dateTo) return false;
    return true;
  };
  const matchPay = (x) =>
    payFilter === "todo" ? true : payFilter === "pagados" ? (x.balance || 0) <= 0.009 : (x.balance || 0) > 0.009;

  const fFacturas = facturas.filter((f) => inRange(f.created_at) && matchPay(f));
  const fRemisiones = remisiones.filter((r) => inRange(r.created_at) && matchPay(r));
  const fPayments = payments.filter((p) => inRange(p.date));

  const sum = (arr, k) => arr.reduce((a, x) => a + (x[k] || 0), 0);
  const tTotal = num(sum(fFacturas, "total") + sum(fRemisiones, "total"));
  const tCobrado = num(sum(fFacturas, "amount_paid") + sum(fRemisiones, "amount_paid"));
  const tSaldo = num(sum(fFacturas, "balance") + sum(fRemisiones, "balance"));
  const tPagos = num(sum(fPayments, "amount"));

  const payLabel = { todo: "Todo el historial", pagados: "Solo pagados", pendientes: "Solo pendientes" }[payFilter];
  const periodo = dateFrom || dateTo ? `${dateFrom || "…"} a ${dateTo || "…"}` : "Todo el periodo";
  const fileBase = `Estado-de-cuenta-${nombre.replace(/[^a-z0-9]/gi, "-")}-${new Date().toISOString().slice(0, 10)}`;

  // ---------------- Exportar PDF ----------------
  const descargarPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const azul = [70, 91, 127];
    const rojo = [237, 31, 38];

    doc.setFillColor(...azul);
    doc.rect(0, 0, pageW, 4, "F");
    doc.setTextColor(...azul);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("ESTADO DE CUENTA", 14, 18);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(orgId ? `Organización: ${nombre}` : `Cliente: ${nombre}`, 14, 25);
    doc.text(`Emitido: ${fmtDate(new Date().toISOString())}`, 14, 30);
    doc.setTextColor(...azul);
    doc.setFontSize(8);
    doc.text(`Periodo: ${periodo}  ·  Estatus: ${payLabel}`, 14, 34.5);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`Saldo del filtro: ${money(tSaldo)}`, pageW - 14, 22, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text("Importe − Cobrado (según filtro)", pageW - 14, 27, { align: "right" });

    let y = 40;
    const docRows = (arr) => arr.length
      ? arr.map((f) => [f.number, fmtDate(f.created_at), fmtDate(f.due_date), money(f.total), money(f.amount_paid), money(f.balance), f.status])
      : [[{ content: "Sin documentos para el filtro seleccionado.", colSpan: 7, styles: { halign: "center", textColor: [120, 120, 120] } }]];

    const table = (title, rows) => {
      doc.setTextColor(...azul);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(title, 14, y);
      autoTable(doc, {
        startY: y + 2,
        head: [["Folio", "Fecha", "Vence", "Total", "Cobrado", "Saldo", "Estatus"]],
        body: rows,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 1.5, font: "helvetica" },
        headStyles: { fillColor: azul, textColor: 255, fontSize: 8 },
        columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 8;
    };

    table("FACTURAS", docRows(fFacturas));
    table("REMISIONES", docRows(fRemisiones));

    // Cruce: pagos aplicados
    doc.setTextColor(...azul);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("PAGOS APLICADOS", 14, y);
    autoTable(doc, {
      startY: y + 2,
      head: [["Fecha", "Documento", "Método", "Banco", "Referencia", "Monto"]],
      body: fPayments.length
        ? fPayments.map((p) => [fmtDate(p.date), p.invoice_number || "—", p.method || "—", p.bank || "—", p.reference || "—", money(p.amount)])
        : [[{ content: "Sin pagos en el periodo seleccionado.", colSpan: 6, styles: { halign: "center", textColor: [120, 120, 120] } }]],
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1.5, font: "helvetica" },
      headStyles: { fillColor: [47, 143, 126], textColor: 255, fontSize: 8 },
      columnStyles: { 5: { halign: "right" } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;

    const resumen = [
      ["Importe total", money(tTotal)],
      ["Cobrado", money(tCobrado)],
      ["Pagos aplicados", money(tPagos)],
      ["Saldo total", money(tSaldo)],
    ];
    autoTable(doc, {
      startY: y,
      body: resumen,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 1.5, font: "helvetica" },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 60 }, 1: { halign: "right" } },
      margin: { left: pageW - 110, right: 14 },
      didParseCell: (d) => {
        if (d.row.index === resumen.length - 1) { d.cell.styles.textColor = rojo; d.cell.styles.fontStyle = "bold"; }
      },
    });
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Incluye ${fFacturas.length} factura(s), ${fRemisiones.length} remisión(es) y ${fPayments.length} pago(s).`, pageW - 14, doc.lastAutoTable.finalY + 5, { align: "right" });

    doc.save(`${fileBase}.pdf`);
  };

  // ---------------- Exportar Excel ----------------
  const descargarExcel = async () => {
    try {
      const headers = ["Tipo", "Folio", "Fecha", "Vence", "Total", "Cobrado", "Saldo", "Estatus"];
      const rows = [
        ...fFacturas.map((f) => ["Factura", f.number, fmtDate(f.created_at), fmtDate(f.due_date), num(f.total), num(f.amount_paid), num(f.balance), f.status]),
        ...fRemisiones.map((r) => ["Remisión", r.number, fmtDate(r.created_at), fmtDate(r.due_date), num(r.total), num(r.amount_paid), num(r.balance), r.status]),
      ];
      rows.push(["TOTAL", "", "", "", tTotal, tCobrado, tSaldo, ""]);
      const summary = {
        title: "Pagos aplicados",
        headers: ["Fecha", "Documento", "Método", "Banco", "Referencia", "Monto"],
        rows: fPayments.length
          ? fPayments.map((p) => [fmtDate(p.date), p.invoice_number || "", p.method || "", p.bank || "", p.reference || "", num(p.amount)])
          : [["—", "Sin pagos en el periodo", "", "", "", 0]],
      };
      await exportToExcel(fileBase, "Estado de cuenta", headers, rows, summary);
    } catch {
      toast.error("No se pudo exportar a Excel");
    }
  };

  const rows = tab === "facturas" ? fFacturas : tab === "remisiones" ? fRemisiones : null;

  return (
    <>
      <PageHeader
        overline={orgId ? "Estado de cuenta · Organización" : "Estado de cuenta"}
        title={nombre}
        action={
          <div className="flex flex-wrap items-center gap-3">
            <Select value={orgId} onChange={(e) => setOrgId(e.target.value)} testid="ec-org-filter">
              <option value="">Solo este cliente</option>
              {orgs.map((o) => (<option key={o.id} value={o.id}>Org: {o.name}</option>))}
            </Select>
            <Btn variant="outline" onClick={() => navigate(-1)} data-testid="back-cobranza"><ArrowLeft size={16} /> Volver</Btn>
            <Btn variant="outline" onClick={descargarExcel} data-testid="estado-excel"><FileSpreadsheet size={16} /> Excel</Btn>
            <Btn onClick={descargarPDF} data-testid="estado-pdf"><Download size={16} /> PDF</Btn>
          </div>
        }
      />
      <div className="p-10 space-y-6">
        {/* Barra de filtros */}
        <div className="border border-[#E3E8EE] bg-white p-4 flex flex-wrap items-end gap-4" data-testid="ec-filter-bar">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold uppercase tracking-wide text-[#16202B]">Desde</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ec-date-from"
              className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold uppercase tracking-wide text-[#16202B]">Hasta</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ec-date-to"
              className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold uppercase tracking-wide text-[#16202B]">Estatus de pago</span>
            <select value={payFilter} onChange={(e) => setPayFilter(e.target.value)} data-testid="ec-pay-filter"
              className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm min-w-[12rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
              <option value="todo">Todo el historial</option>
              <option value="pagados">Solo pagados</option>
              <option value="pendientes">Solo pendientes</option>
            </select>
          </label>
          {(dateFrom || dateTo || payFilter !== "todo") && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); setPayFilter("todo"); }} data-testid="ec-filter-clear"
              className="text-xs uppercase tracking-wider text-[#5B6672] hover:text-[#16202B] px-2 py-2">Limpiar</button>
          )}
        </div>

        <div className="border border-[#465B7F] bg-[#465B7F] text-white p-6 flex items-center justify-between" data-testid="ec-saldo-total-card">
          <div>
            <div className="overline text-white/70">Saldo del periodo</div>
            <div className="font-mono-data text-xs text-white/60 mt-0.5">Importe − Cobrado (según filtros)</div>
          </div>
          <div className="font-mono-data font-bold text-3xl" data-testid="ec-saldo-total">{mxn(tSaldo)}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border border-[#E3E8EE] bg-white">
          <Metric label="Importe total" value={mxn(tTotal)} />
          <Metric label="Cobrado" value={mxn(tCobrado)} success border />
          <Metric label="Pagos aplicados" value={mxn(tPagos)} border />
          <Metric label="Saldo total" value={mxn(tSaldo)} danger />
        </div>

        <div className="flex items-center gap-1 border-b border-[#E3E8EE]">
          <Tab active={tab === "facturas"} onClick={() => setTab("facturas")} testid="tab-facturas">Facturas ({fFacturas.length})</Tab>
          <Tab active={tab === "remisiones"} onClick={() => setTab("remisiones")} testid="tab-remisiones">Remisiones ({fRemisiones.length})</Tab>
          <Tab active={tab === "pagos"} onClick={() => setTab("pagos")} testid="tab-pagos">Pagos aplicados ({fPayments.length})</Tab>
        </div>

        {tab !== "pagos" ? (
          <div className="border border-[#E3E8EE] bg-white data-scroll">
            <table className="w-full">
              <thead><tr className="border-b border-[#E3E8EE] text-left">
                <Th>Folio</Th><Th>Fecha</Th><Th>Vence</Th><Th className="text-right">Total</Th>
                <Th className="text-right">Cobrado</Th><Th className="text-right">Saldo</Th><Th>Estatus</Th><Th className="text-right">PDF</Th>
              </tr></thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><Td className="py-8 text-center" colSpan={8}>Sin documentos para este filtro.</Td></tr>
                ) : rows.map((f) => (
                  <tr key={f.id} data-testid={`ec-${tab === "facturas" ? "invoice" : "remision"}-${f.number}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3]">
                    <Td className="text-[#16202B] font-medium">{f.number}</Td>
                    <Td>{fmtDate(f.created_at)}</Td>
                    <Td>{fmtDate(f.due_date)}</Td>
                    <Td className="text-right">{mxn(f.total)}</Td>
                    <Td className="text-right text-[#49AC9A]">{mxn(f.amount_paid)}</Td>
                    <Td className="text-right text-[#ED1F26]">{mxn(f.balance)}</Td>
                    <Td><StatusBadge status={f.status} /></Td>
                    <Td className="text-right"><a href={`/imprimir/${tab === "facturas" ? "factura" : "remision"}/${f.id}`} target="_blank" rel="noreferrer" className="text-[#5B6672] hover:text-[#465B7F]"><FileText size={15} /></a></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-[#E3E8EE] bg-white data-scroll">
            <table className="w-full">
              <thead><tr className="border-b border-[#E3E8EE] text-left">
                <Th>Fecha</Th><Th>Documento</Th><Th>Método</Th><Th>Banco</Th><Th>Referencia</Th><Th className="text-right">Monto</Th><Th className="text-right">PDF</Th>
              </tr></thead>
              <tbody>
                {fPayments.length === 0 ? (
                  <tr><Td className="py-8 text-center" colSpan={7}>Sin pagos en el periodo seleccionado.</Td></tr>
                ) : fPayments.map((p) => {
                  const tipo = p.target_type === "remision" ? "remision" : "factura";
                  const docId = p.remision_id || p.invoice_id;
                  return (
                  <tr key={p.id} data-testid={`ec-payment-${p.id}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3]">
                    <Td>{fmtDate(p.date)}</Td>
                    <Td className="text-[#16202B] font-medium">{p.invoice_number || "—"}</Td>
                    <Td>{p.method || "—"}</Td>
                    <Td>{p.bank || "—"}</Td>
                    <Td>{p.reference || "—"}</Td>
                    <Td className="text-right text-[#49AC9A]">{mxn(p.amount)}</Td>
                    <Td className="text-right">{docId ? <a href={`/imprimir/${tipo}/${docId}`} target="_blank" rel="noreferrer" className="text-[#5B6672] hover:text-[#465B7F]"><FileText size={15} /></a> : "—"}</Td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Metric({ label, value, success, danger, border }) {
  const color = danger ? "text-[#ED1F26]" : success ? "text-[#49AC9A]" : "text-[#16202B]";
  return (
    <div className={`p-6 ${border ? "border-l border-[#E3E8EE]" : ""}`}>
      <div className="overline">{label}</div>
      <div className={`font-mono-data font-bold text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}
function Tab({ children, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-3 text-sm uppercase tracking-wider border-b-2 -mb-px ${active ? "border-[#465B7F] text-[#16202B]" : "border-transparent text-[#5B6672] hover:text-[#16202B]"}`}>
      {children}
    </button>
  );
}
function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={`font-mono-data text-sm text-[#5B6672] px-4 py-3 ${className}`}>{children}</td>;
}
